import { StyleProvider } from '@ant-design/cssinjs';
import { ConfigProvider, theme } from 'antd';
import type { NextComponentType } from 'next';
import { Session } from 'next-auth';
import { AppProps } from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import { appWithI18Next } from 'ni18n';
import 'quill-emoji/dist/quill-emoji.css';
import 'quill-mention/dist/quill.mention.css';
import 'quill/dist/quill.snow.css';
import React, { FunctionComponent, PropsWithChildren, ReactNode, memo, useEffect, useRef } from 'react';
import { SWRConfig } from 'swr';
import '../customDebugLogger';
import { ni18nConfig } from '../ni18n.config';
import withErrorBoundary from '../src/components/Error/withErrorBoundary';
import {
    FacebookPixelBase,
    FacebookPixelController,
    GoogleTagManagerBase,
    GoogleTagManagerController
} from '../src/components/Meta';
import NetworkStatusController from '../src/components/NetworkStatusController';
import { DEDUP_INTERVAL_DEFAULT } from '../src/constants/swr-config';
import RouteChangeSpinner from '../src/containers/RouteChangeSpinnerContainer';
import TenantAvailabilityController from '../src/containers/TenantAvailablityContainer';
import AuthenticatedPage from '../src/containers/auth/AuthenticatedPage';
import AuthenticationGate from '../src/containers/auth/AuthenticationGate';
import AuthorizationGate from '../src/containers/auth/AuthorizationGate';
import PublicPage from '../src/containers/auth/PublicPage';
import SessionController from '../src/containers/auth/SessionController';
import SubscriptionController from '../src/containers/subscription/SubscriptionController';
import useAlertOnRefresh from '../src/hooks/useAlertOnRefresh';
import useCompactMode from '../src/hooks/useCompactMode';
import GrispiLoadingLayout from '../src/layouts/GrispiLoadingLayout';
import { roles } from '../src/model/types';
import LanguageProvider from '../src/store/provider/LanguageProvider';
import useNotificationStore from '../src/store/useNotificationStore';
import '../src/styles/global.css';
import { defaultThemeConfig } from '../src/styles/theme';
import { getTenantId } from '../src/utils/accountHelper';
import { endUserPublicFetcher, fetcherWithToken } from '../src/utils/fetchers';
import { isSSR, nullishValue } from '../src/utils/helpers';
import {
    StorageHelperKeys,
    getLocalStorageItem,
    removeAllStorageItems,
    setLocalStorageItem
} from '../src/utils/storageHelpers';
import { SWRMiddleware } from '../src/utils/swr';
import { assignTabId } from '../src/utils/tabId';

type GrispiNextComponent = (NextComponentType | ReactNode) & {
    auth?: { role: roles; loadingLayout: ReactNode };
    getLayout?: <T extends GrispiNextComponent>(Component: T) => T;
};
type GrispiAppProps<P = { session?: Session }> = AppProps<P> & {
    Component: GrispiNextComponent;
};

if (isSSR()) React.useLayoutEffect = React.useEffect; // https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85#gistcomment-3886909

function useDebugEffect(componentName: string) {
    const isMountedRef = useRef(false);
    useEffect(() => {
        isMountedRef.current && console.debug(`${componentName} re-rendered`);
    });
    useEffect(() => {
        console.debug(`${componentName} is mounting`);
        isMountedRef.current = true;
        return () => {
            console.debug(`${componentName} is unmounting`);
            isMountedRef.current = false;
        };
    }, [componentName]);
}

function useRefreshedStorage() {
    useEffect(() => {
        if (Router.pathname === '/login-page') {
            document.querySelectorAll('iframe').forEach((el) => el.remove());
            removeAllStorageItems();
        }
        setLocalStorageItem(StorageHelperKeys.TENANT_ID, getTenantId());
        !getLocalStorageItem(StorageHelperKeys.ACTION_AFTER_SUBMIT) &&
            setLocalStorageItem(StorageHelperKeys.ACTION_AFTER_SUBMIT, 'stay');
        nullishValue(getLocalStorageItem(StorageHelperKeys.SHOW_QUICK_FLOWS)) &&
            setLocalStorageItem(StorageHelperKeys.SHOW_QUICK_FLOWS, true);
        assignTabId();
    });

    useEffect(() => {
        getLocalStorageItem(StorageHelperKeys.NEW_USER) && setLocalStorageItem(StorageHelperKeys.NEW_USER, false);
        nullishValue(getLocalStorageItem(StorageHelperKeys.NEW_USER)) &&
            setLocalStorageItem(StorageHelperKeys.NEW_USER, true);
    }, []);
}

function UtilityController() {
    useAlertOnRefresh();
    useRefreshedStorage();

    return null;
}

function ThemeAlgorithmsController({ children }: PropsWithChildren) {
    const isCompactMode = useCompactMode();

    return (
        <ConfigProvider
            theme={{
                ...defaultThemeConfig,
                algorithm: isCompactMode ? theme.compactAlgorithm : theme.defaultAlgorithm
            }}
        >
            {children}
            <MemoizedNotificationContextProvider />
        </ConfigProvider>
    );
}

const MemoizedNotificationContextProvider = memo(NotificationContextProvider);
function NotificationContextProvider() {
    const { dynamicMessageContextHolder, dynamicNotificationContextHolder } = useNotificationStore();
    return (
        <>
            {dynamicMessageContextHolder}
            {dynamicNotificationContextHolder}
        </>
    );
}

// App component is MOUNTED at the beginning and is PERSISTED in the DOM during the application lifecycle
// App component will be RE-RENDERED when navigating application pages but will not be UNMOUNTED
function App({ Component, pageProps: { session, ...pageProps } }: GrispiAppProps) {
    // Use the layout defined at the page level, if available
    const getLayout = Component.getLayout ?? ((page) => page);

    return (
        <StyleProvider hashPriority="high">
            <ThemeAlgorithmsController>
                <NetworkStatusController />
                <RouteChangeSpinner>
                    <Head>
                        {Component.auth && <FacebookPixelBase />}
                        <meta name="viewport" content="width=device-width, initial-scale=1" />
                        <title>Grispi</title>
                    </Head>
                    {Component.auth && <GoogleTagManagerBase />}
                    {Component.auth && <GoogleTagManagerController />}
                    {Component.auth && <FacebookPixelController />}
                    <UtilityController />
                    <TenantAvailabilityController>
                        <SessionController session={session}>
                            <SWRConfig
                                value={{
                                    fetcher: Component.auth ? fetcherWithToken : endUserPublicFetcher,
                                    dedupingInterval: DEDUP_INTERVAL_DEFAULT,
                                    revalidateIfStale: false,
                                    revalidateOnFocus: false,
                                    use: [SWRMiddleware]
                                }}
                            >
                                <LanguageProvider>
                                    {Component.auth ? (
                                        <AuthenticationGate
                                            loadingLayout={Component.auth.loadingLayout || <GrispiLoadingLayout />}
                                        >
                                            <AuthenticatedPage>
                                                <SubscriptionController>
                                                    <AuthorizationGate role={Component.auth.role}>
                                                        {getLayout(<Component {...pageProps} />)}
                                                    </AuthorizationGate>
                                                </SubscriptionController>
                                            </AuthenticatedPage>
                                        </AuthenticationGate>
                                    ) : (
                                        <PublicPage>{getLayout(<Component {...pageProps} />)}</PublicPage>
                                    )}
                                </LanguageProvider>
                            </SWRConfig>
                        </SessionController>
                    </TenantAvailabilityController>
                </RouteChangeSpinner>
            </ThemeAlgorithmsController>
        </StyleProvider>
    );
}

export default withErrorBoundary(appWithI18Next(App, ni18nConfig) as FunctionComponent);

export { useDebugEffect, type GrispiNextComponent };
